import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, Textarea, ErrorSummary, Select, ActionLink } from 'nhsuk-react-components';
import { listImplantHistories, createImplantHistory, updateImplantHistory, deleteImplantHistory, getImplantHistoryDetails } from '../../actions/implantHistoryActions';
import { IMPLANT_HISTORY_CREATE_RESET, IMPLANT_HISTORY_UPDATE_RESET } from '../../constants/implantHistoryConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import ISODateInput from '../../components/date/ISODateInput.component';
import Modal from '../../components/modal/Modal.component';
import QRCode from "react-qr-code";
import PageWrapper from '../../components/layout/PageWrapper.component';

const emptyImplantHistory = {
    patientName: '',
    patientNHSNumber: '',
    interventionCategory: '',
    eventDate: new Date().toISOString(),
    eventType: '',
    eventDetails: '',
    practitionerDetails: '',
    notes: '',
};

const interventionCategoryOptions = (
    <>
        <Select.Option value="">Select an intervention category</Select.Option>
        <Select.Option value="No intervention (FU only of pt implanted elsewhere)">No intervention (FU only of pt implanted elsewhere)</Select.Option>
        <Select.Option value="First ever pacemaker/ICD system">First ever pacemaker/ICD system</Select.Option>
        <Select.Option value="Generator change only">Generator change only</Select.Option>
        <Select.Option value="New generator and lead(s) revision/replacement">New generator and lead(s) revision/replacement</Select.Option>
        <Select.Option value="Upgrade - increased functionality (to DDD, CRT, or ICD)">Upgrade - increased functionality (to DDD, CRT, or ICD)</Select.Option>
        <Select.Option value="Downgrade - decreased functionality">Downgrade - decreased functionality</Select.Option>
        <Select.Option value="System explant">System explant</Select.Option>
        <Select.Option value="Wound revision/generator resite/haematoma">Wound revision/generator resite/haematoma</Select.Option>
        <Select.Option value="Lead intervention only">Lead intervention only</Select.Option>
        <Select.Option value="Monitor procedure only">Monitor procedure only</Select.Option>
        <Select.Option value="Log device complication">Log device complication</Select.Option>
        <Select.Option value="Implant alerts">Implant alerts</Select.Option>
        <Select.Option value="Device Advisories">Device Advisories</Select.Option>
        <Select.Option value="Other">Other</Select.Option>
    </>
);

const eventTypeOptions = (
    <>
        <Select.Option value="">Select an event type</Select.Option>
        <Select.Option value="Implant">Implant</Select.Option>
        <Select.Option value="Revision">Revision</Select.Option>
        <Select.Option value="Removal">Removal</Select.Option>
        <Select.Option value="Lead Extraction">Lead Extraction</Select.Option>
        <Select.Option value="Lead Implant">Lead Implant</Select.Option>
        <Select.Option value="Lead Revision">Lead Revision</Select.Option>
        <Select.Option value="Lead Removal">Lead Removal</Select.Option>
        <Select.Option value="Generator Implant">Generator Implant</Select.Option>
        <Select.Option value="Generator Revision">Generator Revision</Select.Option>
        <Select.Option value="Generator Removal">Generator Removal</Select.Option>
        <Select.Option value="Generator Replacement">Generator Replacement</Select.Option>
        <Select.Option value="Other">Other</Select.Option>
    </>
);

export default function ImplantHistoryCRUDScreen({ history }) {
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Edit Implant Alerts";
    }, []);

    useEffect(() => {
        dispatch({ type: IMPLANT_HISTORY_CREATE_RESET });
        dispatch({ type: IMPLANT_HISTORY_UPDATE_RESET });
    }, [dispatch]);

    const [implantHistoryListFilter, setimplantHistoryListFilter] = useState({ query: '', page: 1, filter: 'patientName' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newImplantHistory, setNewImplantHistory] = useState({ ...emptyImplantHistory });
    const [updatedImplantHistory, setUpdatedImplantHistory] = useState({});

    const implantHistoryList = useSelector((state) => state.implantHistoryList);
    const { loading, error, implantHistories, currentPage, totalPages, totalCount } = implantHistoryList;

    const implantHistoryCreate = useSelector((state) => state.implantHistoryCreate);
    const { success: successCreate, error: implantHistoryCreateErrors, loading: implantHistoryCreateLoading } = implantHistoryCreate;

    const implantHistoryUpdate = useSelector((state) => state.implantHistoryUpdate);
    const { success: successUpdate, loading: implantHistoryUpdateProcessing } = implantHistoryUpdate;

    const implantHistoryDetails = useSelector((state) => state.implantHistoryDetails);
    const { implantHistory: implantHistoryDet, loading: implantHistoryDetailsLoading } = implantHistoryDetails;

    useEffect(() => {
        dispatch(listImplantHistories(implantHistoryListFilter));
    }, [dispatch, implantHistoryListFilter]);

    useEffect(() => {
        if (implantHistoryDet) {
            setUpdatedImplantHistory({ ...implantHistoryDet });
        }
    }, [implantHistoryDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewImplantHistory({
                ...emptyImplantHistory,
                eventDate: new Date().toISOString(),
            });
            dispatch(listImplantHistories(implantHistoryListFilter));
            dispatch({ type: IMPLANT_HISTORY_CREATE_RESET });
        }
    }, [successCreate, dispatch, implantHistoryListFilter]);

    const handlePageChange = (newPage) => {
        setimplantHistoryListFilter({ ...implantHistoryListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setimplantHistoryListFilter({ ...implantHistoryListFilter, query: e.target.value, page: 1 });
    };

    const handleFilterChange = (e) => {
        setimplantHistoryListFilter({ ...implantHistoryListFilter, filter: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this implantHistory?')) {
            dispatch(deleteImplantHistory(id)).then(() => {
                dispatch(listImplantHistories(implantHistoryListFilter));
                if (updatedImplantHistory?.id === id) {
                    setShowEditModal(false);
                    setUpdatedImplantHistory({});
                }
            });
        }
    };

    const handleEditImplantHistory = (id) => {
        dispatch(getImplantHistoryDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedImplantHistory({});
        dispatch({ type: IMPLANT_HISTORY_UPDATE_RESET });
    };

    const handleCreateImplantHistory = () => {
        dispatch(createImplantHistory(newImplantHistory));
    };

    const handleUpdateImplantHistory = () => {
        dispatch(updateImplantHistory(updatedImplantHistory.id, updatedImplantHistory)).then(() => {
            dispatch(getImplantHistoryDetails(updatedImplantHistory.id));
            dispatch(listImplantHistories(implantHistoryListFilter));
        });
    };

    const openCreateModal = () => {
        setNewImplantHistory({
            ...emptyImplantHistory,
            eventDate: new Date().toISOString(),
        });
        dispatch({ type: IMPLANT_HISTORY_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="Implant Alerts" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <Card>
                                    <Card.Content>
                                        <Card.Heading>Log Device Complications</Card.Heading>
                                        <Row gutter={[24, 24]}>
                                            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                                                <div className="nhsuk-card nhsuk-card--clickable">
                                                    <div className="nhsuk-card__content">
                                                        <QRCode
                                                            value="https://forms.office.com/e/JmQfDTPZSR"
                                                            style={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                maxWidth: '200px',
                                                                display: 'block',
                                                                margin: '0 auto'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                                                <p className="nhsuk-body">
                                                    We have implemented a streamlined process for reporting device complications, recently approved by the EP board. This form allows any MDT member to log complications via the QR code above or link below, taking <strong>less than a minute</strong> to complete.
                                                </p>

                                                <div className="nhsuk-u-margin-top-4">
                                                    <ActionLink
                                                        href="https://forms.office.com/e/JmQfDTPZSR"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Open Complications Form
                                                    </ActionLink>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Content>
                                </Card>
                                <Button
                                    className="nhsuk-button--small nhsuk-u-margin-bottom-4 nhsuk-u-margin-top-4"
                                    onClick={openCreateModal}
                                >
                                    + Create Alert
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-implantHistories"
                                        label="Search Records"
                                        name="search-implantHistories"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                    <Select
                                        id="filter-implant-histories"
                                        label="Filter by"
                                        name="filter-implant-histories"
                                        onChange={handleFilterChange}
                                    >
                                        <Select.Option value="Patientname">Patient full name</Select.Option>
                                        <Select.Option value="PatientNHSNumber">Patient NHS Number</Select.Option>
                                    </Select>
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {implantHistories && implantHistories.length > 0 ? (
                                            implantHistories.map((implantHistory) => (
                                                <Details key={implantHistory.id}>
                                                    <Details.Summary>
                                                        {implantHistory.patientName}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Patient Name:</strong> {implantHistory.patientName}
                                                        </p>
                                                        <p>
                                                            <strong>Patient NHS Number:</strong> {implantHistory.patientNHSNumber}
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={implantHistoryDetailsLoading}
                                                                onClick={() => handleEditImplantHistory(implantHistory.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(implantHistory.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: IMPLANT_HISTORY_CREATE_RESET });
                }}
                theme="light"
                maxWidth="720px"
            >
                <h2 className="nhsuk-heading-l">Create Alert</h2>
                {implantHistoryCreateErrors && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {implantHistoryCreateErrors.map((err) => (
                                    <li key={err}>{err}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Input
                    id="patientName-create"
                    label="Patient Name"
                    name="patientName-create"
                    type="text"
                    onChange={(e) => setNewImplantHistory({ ...newImplantHistory, patientName: e.target.value })}
                    value={newImplantHistory?.patientName || ''}
                    required
                />
                <Input
                    id="patientNHSNumber-create"
                    label="Patient NHS Number"
                    name="patientNHSNumber-create"
                    type="text"
                    onChange={(e) => setNewImplantHistory({ ...newImplantHistory, patientNHSNumber: e.target.value })}
                    value={newImplantHistory?.patientNHSNumber || ''}
                    required
                />
                <Select
                    id="interventionCategory-create"
                    label="Intervention Category"
                    name="interventionCategory-create"
                    onChange={(e) => setNewImplantHistory({ ...newImplantHistory, interventionCategory: e.target.value })}
                    value={newImplantHistory?.interventionCategory || ''}
                    required
                >
                    {interventionCategoryOptions}
                </Select>
                <Select
                    id="eventType-create"
                    label="Event Type"
                    name="eventType-create"
                    onChange={(e) => setNewImplantHistory({ ...newImplantHistory, eventType: e.target.value })}
                    value={newImplantHistory?.eventType || ''}
                    required
                >
                    {eventTypeOptions}
                </Select>
                <ISODateInput
                    id="eventDateTime-create"
                    label="Event Date"
                    name="eventDateTime-create"
                    value={newImplantHistory?.eventDate}
                    onChange={(eventDate) => setNewImplantHistory({ ...newImplantHistory, eventDate })}
                    includeTime={true}
                    required
                />
                <Textarea
                    id="eventDetails-create"
                    label="Event Details"
                    name="eventDetails-create"
                    onChange={(e) => setNewImplantHistory({ ...newImplantHistory, eventDetails: e.target.value })}
                    value={newImplantHistory?.eventDetails || ''}
                />
                <Textarea
                    id="practitionerDetails-create"
                    label="Practitioner Details"
                    name="practitionerDetails-create"
                    onChange={(e) => setNewImplantHistory({ ...newImplantHistory, practitionerDetails: e.target.value })}
                    value={newImplantHistory?.practitionerDetails || ''}
                />
                <Textarea
                    id="notes-create"
                    label="Notes"
                    name="notes-create"
                    onChange={(e) => setNewImplantHistory({ ...newImplantHistory, notes: e.target.value })}
                    value={newImplantHistory?.notes || ''}
                />
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button
                        className="nhsuk-button--small"
                        disabled={implantHistoryCreateLoading || implantHistoryUpdateProcessing}
                        onClick={handleCreateImplantHistory}
                    >
                        Create Alert
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: IMPLANT_HISTORY_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="720px"
            >
                <h2 className="nhsuk-heading-l">Edit Alert</h2>
                {implantHistoryDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        <Input
                            id="patientName"
                            label="Patient Name"
                            name="patientName"
                            type="text"
                            onChange={(e) => setUpdatedImplantHistory({ ...updatedImplantHistory, patientName: e.target.value })}
                            value={updatedImplantHistory?.patientName || ''}
                            required
                        />
                        <Input
                            id="patientNHSNumber"
                            label="Patient NHS Number"
                            name="patientNHSNumber"
                            type="text"
                            onChange={(e) => setUpdatedImplantHistory({ ...updatedImplantHistory, patientNHSNumber: e.target.value })}
                            value={updatedImplantHistory?.patientNHSNumber || ''}
                            required
                        />
                        <Select
                            id="interventionCategory"
                            label="Intervention Category"
                            name="interventionCategory"
                            onChange={(e) => setUpdatedImplantHistory({ ...updatedImplantHistory, interventionCategory: e.target.value })}
                            value={updatedImplantHistory?.interventionCategory || ''}
                            required
                        >
                            {interventionCategoryOptions}
                        </Select>
                        <Select
                            id="eventType"
                            label="Event Type"
                            name="eventType"
                            onChange={(e) => setUpdatedImplantHistory({ ...updatedImplantHistory, eventType: e.target.value })}
                            value={updatedImplantHistory?.eventType || ''}
                            required
                        >
                            {eventTypeOptions}
                        </Select>
                        <ISODateInput
                            id="updateEventDateTime"
                            label="Event Date"
                            name="eventDateTime"
                            value={updatedImplantHistory?.eventDate}
                            onChange={(eventDate) => setUpdatedImplantHistory({ ...updatedImplantHistory, eventDate })}
                            includeTime={true}
                            required
                        />
                        <Textarea
                            id="eventDetails"
                            label="Event Details"
                            name="eventDetails"
                            onChange={(e) => setUpdatedImplantHistory({ ...updatedImplantHistory, eventDetails: e.target.value })}
                            value={updatedImplantHistory?.eventDetails || ''}
                        />
                        <Textarea
                            id="practitionerDetails"
                            label="Practitioner Details"
                            name="practitionerDetails"
                            onChange={(e) => setUpdatedImplantHistory({ ...updatedImplantHistory, practitionerDetails: e.target.value })}
                            value={updatedImplantHistory?.practitionerDetails || ''}
                        />
                        <Textarea
                            id="notes"
                            label="Notes"
                            name="notes"
                            onChange={(e) => setUpdatedImplantHistory({ ...updatedImplantHistory, notes: e.target.value })}
                            value={updatedImplantHistory?.notes || ''}
                        />
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The implantHistory was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button
                                className="nhsuk-button--small"
                                disabled={implantHistoryUpdateProcessing}
                                onClick={handleUpdateImplantHistory}
                            >
                                Save changes
                            </Button>
                            <Button
                                secondary
                                className="nhsuk-button--small"
                                onClick={handleCloseEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
